import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Building2, FileSignature, Wrench, BookOpen, HardHat, ShieldCheck, LogOut, Menu, Home, Receipt, Gauge, ShieldPlus, FolderOpen, ConciergeBell, CalendarClock, BarChart3, CalendarRange, Navigation } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth, usePrefs } from "@/lib/context";
import { TopBar } from "@/components/TopBar";
import { OfflineBanner } from "@/components/InstallApp";
import { GpsShareProvider } from "@/components/GpsShare";

const MGMT = ["admin", "supervision", "gestionnaire", "proprietaire"];
const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, roles: "*", sec: "secGeneral" },
  { to: "/properties", key: "properties", icon: Building2, roles: MGMT, sec: "secAsset" },
  { to: "/leases", key: "leases", icon: FileSignature, roles: MGMT, sec: "secAsset" },
  { to: "/leases", key: "myHome", icon: Home, roles: ["locataire"], sec: "secAsset" },
  { to: "/charges", key: "meterCharges", icon: Gauge, roles: MGMT, sec: "secAsset" },
  { to: "/contracts", key: "contracts", icon: ShieldPlus, roles: MGMT, sec: "secAsset" },
  { to: "/documents", key: "docs", icon: FolderOpen, roles: [...MGMT, "locataire"], sec: "secAsset" },
  { to: "/tickets", key: "tickets", icon: Wrench, roles: "*", sec: "secOps" },
  { to: "/concierge", key: "concierge", icon: ConciergeBell, roles: "*", sec: "secOps" },
  { to: "/tracking", key: "liveTracking", icon: Navigation, roles: "*", sec: "secOps" },
  { to: "/planning", key: "planning", icon: CalendarRange, roles: ["admin", "supervision", "gestionnaire", "prestataire"], sec: "secOps" },
  { to: "/maintenance", key: "maintenance", icon: CalendarClock, roles: MGMT, sec: "secOps" },
  { to: "/faq", key: "faq", icon: BookOpen, roles: "*", sec: "secOps" },
  { to: "/finance", key: "finance", icon: Receipt, roles: "*", sec: "secFinance" },
  { to: "/reports", key: "reports", icon: BarChart3, roles: MGMT, sec: "secFinance" },
  { to: "/provider", key: "provider", icon: HardHat, roles: ["prestataire"], sec: "secGeneral" },
  { to: "/admin", key: "admin", icon: ShieldCheck, roles: ["admin", "supervision"], sec: "secGeneral" },
];

function SideNav({ onNavigate }) {
  const { user, logout } = useAuth();
  const { t } = usePrefs();
  const items = NAV.filter((n) => n.roles === "*" || n.roles.includes(user.role));
  return (
    <div className="flex h-full flex-col bg-[#0F172A] text-slate-300">
      <div className="px-6 pb-4 pt-7">
        <div className="font-display text-2xl font-bold tracking-[0.12em] text-white">GESTIMMO</div>
        <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#B8860B]">{t("tagline")}</div>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3">
        {items.map((n, i) => (
          <div key={n.key}>
          {(i === 0 || items[i - 1].sec !== n.sec) && i > 0 && <div className="px-3 pb-1 pt-4 text-[9px] font-bold uppercase tracking-[0.22em] text-slate-500">{t(n.sec)}</div>}
          <NavLink to={n.to} end={n.to === "/"} onClick={onNavigate} data-testid={`nav-${n.key}`}
            className={({ isActive }) => `group flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-white/10 text-white shadow-[inset_2px_0_0_#B8860B]" : "hover:bg-white/5 hover:text-white"}`}>
            <n.icon size={17} strokeWidth={1.6} /> {t(n.key)}
          </NavLink>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 px-4 py-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#B8860B] text-sm font-bold text-white">{user.name?.[0]}</div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">{user.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400" data-testid="sidebar-user-role">{t(`role_${user.role}`)}</div>
          </div>
        </div>
        <button onClick={logout} data-testid="logout-button" className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-xs text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
          <LogOut size={14} /> {t("logout")}
        </button>
        <div className="mt-3 text-[10px] italic leading-snug text-slate-500" data-testid="sidebar-signature">{t("signature")}</div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const { lang } = usePrefs();
  return (
    <GpsShareProvider>
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 lg:block"><SideNav /></aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar mobileTrigger={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><button className="lg:hidden" data-testid="mobile-menu-button"><Menu size={22} /></button></SheetTrigger>
            <SheetContent side={lang === "ar" ? "right" : "left"} className="w-64 border-0 p-0"><SideNav onNavigate={() => setOpen(false)} /></SheetContent>
          </Sheet>
        } />
        <OfflineBanner />
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-8 sm:px-8 lg:px-10"><Outlet /></main>
        <footer className="border-t border-slate-200 px-8 py-4 text-center text-[11px] tracking-wide text-slate-400" data-testid="footer-signature">
          Gestimmo © {new Date().getFullYear()} — <span className="italic">Conçue par ELOUNGOU ÉTIENNE LANDRY</span>
        </footer>
      </div>
    </div>
    </GpsShareProvider>
  );
}
